import PhoneForm from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/PhoneForm';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CheckCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Caption, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { usePreventBodyScroll } from '@/components/Header/components/EnableSmsOnSavePrompt/components/EnableSmsOnSavePopover/hooks/PreventBodyScroll/usePreventBodyScroll';
import {
    TrackEnableSmsOnSavePromptActionTaken,
    useTrackEnableSmsOnSavePrompt,
} from '@/utils/amplitude/analytics/notifications';
import { getTermsAndConditionsUrl } from '@/utils/urls';

import { EnableSmsOnSaveForm } from '../../EnableSmsOnSavePrompt';

type Props = {
    onCloseSaveWidget: () => void;
    onOutsideTooltipClick: () => void;
    renderTransition: boolean;
    setShowWidget: (show: boolean) => void;
    showWidget: boolean;
};

export const EnableSmsOnSavePopover = ({
    onCloseSaveWidget,
    onOutsideTooltipClick,
    renderTransition,
    setShowWidget,
    showWidget,
}: Props) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const { formatMessage } = useIntl();

    // Prevent body scroll when popover is shown
    usePreventBodyScroll();

    const {
        control,
        formState: { errors },
        setValue,
        watch,
    } = useFormContext<EnableSmsOnSaveForm>();

    const isFormSubmitAttempted = watch('isFormSubmitAttempted');
    const isValidPhone = watch('isValid');
    const isSubmitLoading = watch('isSubmitLoading');
    const trackEnableSmsOnSavePrompt = useTrackEnableSmsOnSavePrompt();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            const anchor = document.querySelector('#savedItemsIcon');

            // Don't close if clicking on the tooltip content or the anchor element
            if (tooltipRef.current?.contains(target) || anchor?.contains(target)) {
                return;
            }

            onOutsideTooltipClick();
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showWidget, onOutsideTooltipClick, trackEnableSmsOnSavePrompt]);

    const hasPhoneInputError = (Boolean(errors.phoneData?.message) || !isValidPhone) && isFormSubmitAttempted;

    return (
        <div
            className={cn(
                'fixed right-0 top-[98px] z-[1000] h-screen w-screen bg-background-overlay transition-opacity duration-300',
                renderTransition ? 'opacity-100' : 'opacity-0'
            )}
            data-testid="smsOnSavePopover"
            onTransitionEnd={() => {
                if (!renderTransition && showWidget) {
                    setShowWidget(false);
                }
            }}
        >
            <Tooltip
                anchorSelect="#savedItemsIcon"
                className="z-[1000] min-w-[395px] !px-0"
                clickable
                isOpen={showWidget}
            >
                <div
                    className="flex flex-col gap-md p-md"
                    onClick={(e) => e.stopPropagation()}
                    ref={tooltipRef}
                >
                    <div className="flex items-center gap-xs">
                        <CheckCircle
                            color="success"
                            style="fill"
                        />
                        <H5>{formatMessage({ id: 'enableSmsOnSave.title' })}</H5>
                    </div>
                    <div
                        className={cn(
                            'flex flex-col gap-sm',
                            !isValidPhone &&
                                !isFormSubmitAttempted &&
                                '[&_[data-testid="nameContactFormPhoneInput-telInput-spacer"]]:hidden'
                        )}
                    >
                        <H5>{formatMessage({ id: 'enableSmsOnSave.getTextWhenItIsTimeToBidLive' })}</H5>
                        <div>
                            <Controller
                                control={control}
                                name="phoneData"
                                render={({ field: { onChange, value } }) => (
                                    //TODO: migrate to use the new phone form component from Hammer UI https://auctiontechnologygroup.atlassian.net/browse/LAAC-312
                                    //Hiding spacer under the input in the DOM unless there is an error
                                    <div
                                        className={cn(
                                            hasPhoneInputError
                                                ? ''
                                                : "[&_[data-testid='nameContactFormPhoneInput-telInput-spacer']]:hidden"
                                        )}
                                    >
                                        <PhoneForm
                                            errorMessage={formatMessage({ id: 'enableSmsOnSave.phoneNumberError' })}
                                            onChange={(phoneForm, valid) => {
                                                onChange(phoneForm);
                                                setValue('isValid', valid);
                                                setValue('isFormSubmitAttempted', false);
                                            }}
                                            phoneLabel={null}
                                            phoneNumber={value}
                                            placeholder={formatMessage({
                                                id: 'enableSmsOnSave.phoneNumberPlaceholder',
                                            })}
                                            showCountryCode
                                            submitted={isFormSubmitAttempted}
                                            testid="nameContactFormPhoneInput"
                                        />
                                    </div>
                                )}
                            />

                            <div className="mt-2xs">
                                <Caption className="text-text-secondary">
                                    <FormattedMessage
                                        id="leadingBidder.captions.terms"
                                        values={{
                                            termsLink: (
                                                <Link
                                                    className="font-inter text-xs font-normal text-link-primary no-underline hover:no-underline"
                                                    href={getTermsAndConditionsUrl('tcpa-consent-privacy')}
                                                    key="leading-bid-terms-and-conditions-link"
                                                    target="_blank"
                                                >
                                                    <FormattedMessage id="terms_and_conditions" />
                                                </Link>
                                            ),
                                        }}
                                    />
                                </Caption>
                            </div>
                        </div>
                        <Button
                            data-testid="textMeButton"
                            loading={isSubmitLoading}
                            type="submit"
                        >
                            {formatMessage({ id: 'enableSmsOnSave.textMe' })}
                        </Button>
                    </div>
                    <div className="text-center">
                        <InlineButton
                            data-testid="noThanksButton"
                            disabled={isSubmitLoading}
                            onClick={() => {
                                trackEnableSmsOnSavePrompt({
                                    actionTaken: TrackEnableSmsOnSavePromptActionTaken.Rejected,
                                });
                                onCloseSaveWidget();
                            }}
                            type="button"
                        >
                            {formatMessage({ id: 'enableSmsOnSave.noThanksPopover' })}
                        </InlineButton>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};
